import type { LastActive, Pin, PinRing, StatusKind } from '@feral/shared/client'
import { STATUS_KIND_OPTIONS } from '@feral/shared/options/status'

// A person's presence and status in words, the same wherever they're shown (docs/USER-ICONS.md IC-R5):
// the card, the profile, the grid, the hover card, the Inbox and the lists all read from here.

type Status = NonNullable<Pin['status']>

/**
 * Their status while it's still running. A reply fetched before it ran out still carries it, and
 * nothing redraws until the next fetch, so every place asks this rather than trusting the copy.
 */
export function liveStatus<S extends { expiresAt: string }>(
  s: S | null | undefined,
  now = Date.now(),
): S | null {
  return s && new Date(s.expiresAt).getTime() > now ? s : null
}

/** The status's kind, while it runs; browsing counts as none. */
export function statusKind(
  s: Pick<Status, 'kind' | 'expiresAt'> | StatusKind | null | undefined,
): StatusKind | null {
  const kind = typeof s === 'string' ? s : liveStatus(s)?.kind
  return kind && kind !== 'browsing' ? kind : null
}

function ago(iso: string): string {
  const m = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  return h < 24 ? `${h} h ago` : 'yesterday'
}
const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

/**
 * What they're up to: the label alone ("Hosting") on tiles and lists, and where there's room (`long`)
 * "Hosting until 1:30" or "Looking · set 5 min ago". Null when browsing or run out.
 */
export function statusWords(s: Status | StatusKind | null | undefined, long = false): string | null {
  const kind = statusKind(s)
  if (!kind) return null
  // At a spot says which, where the server told this reader (FR-WAL-15).
  const label =
    kind === 'at_spot' && typeof s === 'object' && s?.spot
      ? `At ${s.spot.name}`
      : STATUS_KIND_OPTIONS[kind].label
  if (!long || typeof s !== 'object' || !s) return label
  if (kind === 'hosting') return `${label} until ${clock(s.expiresAt)}`
  if (kind === 'looking') return `${label} · set ${ago(s.setAt)}`
  return label
}

const BUCKET: Record<LastActive, string> = {
  now: 'Online now',
  m15: 'Active in the last 15 min',
  h1: 'Active in the last hour',
  today: 'Active today',
  week: 'Active this week',
  longer: 'Away a while',
}

/**
 * When they were on, matching the ring (IC-01): "Online now", else the minutes when the reply has
 * them (a pin), else its bucket (a profile, a thread), else "Away" (they hide it, or nothing yet).
 */
export function presenceWords(p: {
  ring: PinRing
  lastSeenMin?: number | null
  lastActive?: LastActive | null
}): string {
  if (p.ring === 'online') return 'Online now'
  const m = p.lastSeenMin
  if (m != null) {
    if (m < 60) return `Active ${m} min ago`
    if (m < 24 * 60) return `Active ${Math.round(m / 60)} h ago`
    return `Active ${Math.round(m / (24 * 60))} d ago`
  }
  if (p.lastActive) return BUCKET[p.lastActive]
  return p.ring === 'recent' ? 'Active in the last half hour' : 'Away'
}

const statusKey = (s: Pin['status']) => (s ? `${s.kind}|${s.setAt}|${s.expiresAt}` : '')

/** Who, seen in both of two map replies, has a different status in the second (its kind or its times). */
export function statusChanged(was: readonly Pin[], now: readonly Pin[]): string[] {
  const before = new Map(was.flatMap((p) => (p.id ? [[p.id, statusKey(p.status)] as const] : [])))
  const changed = new Set<string>()
  for (const p of now) {
    const old = p.id ? before.get(p.id) : undefined
    if (p.id && old !== undefined && old !== statusKey(p.status)) changed.add(p.id)
  }
  return [...changed]
}
